/**
 * Context detection for slash menu ranking.
 *
 * Reads the first block of the document to detect doc type from
 * frontmatter-style content (e.g., "type: memoir").
 * Also tracks the active agent for priority ranking.
 */

import type { BlockNoteEditor } from '@blocknote/core';
import type { DocType } from './registry';

/**
 * Try to detect the doc type from the first block content.
 * Looks for patterns like:
 *   type: memoir
 *   type: recipe
 *   ---\ntype: memoir\n---
 */
export function detectDocType(editor: BlockNoteEditor): DocType {
  try {
    const blocks = editor.document;
    if (!blocks || blocks.length === 0) return 'default';

    // Check first block for frontmatter-style type declaration
    const firstBlock = blocks[0];
    if (!firstBlock || !firstBlock.content) return 'default';

    const contentArray = firstBlock.content as Array<{ type: string; text?: string }>;
    const text = contentArray
      .filter((item) => item.type === 'text' && item.text)
      .map((item) => item.text)
      .join('');

    // Match "type: <value>" pattern
    const typeMatch = text.match(/type:\s*(memoir|recipe|science|map|soul)/i);
    if (typeMatch) {
      return typeMatch[1].toLowerCase() as DocType;
    }

    // Also check second block if first is a "---" divider
    if (text.trim() === '---' && blocks.length > 1) {
      const secondBlock = blocks[1];
      if (secondBlock && secondBlock.content) {
        const secondContentArray = secondBlock.content as Array<{
          type: string;
          text?: string;
        }>;
        const secondText = secondContentArray
          .filter((item) => item.type === 'text' && item.text)
          .map((item) => item.text)
          .join('');

        const secondMatch = secondText.match(/type:\s*(memoir|recipe|science|map|soul)/i);
        if (secondMatch) {
          return secondMatch[1].toLowerCase() as DocType;
        }
      }
    }
  } catch {
    // Fail silently -- context detection is best-effort
  }

  return 'default';
}

// ---- Active agent tracking -------------------------------------------------

let _activeAgentId: string | null = null;

export function getActiveAgent(): string | null {
  return _activeAgentId;
}

export function setActiveAgent(agentId: string | null): void {
  _activeAgentId = agentId;
}

// Listen for agent open events to track active agent
if (typeof window !== 'undefined') {
  window.addEventListener('bb-docs:open-agent', ((event: CustomEvent) => {
    setActiveAgent(event.detail?.agentId || null);
  }) as EventListener);
}
